import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { listarPrecosIa, salvarPrecoIa, usoIa, type ProvedorIa } from '../api/admin'
import { NOME_ORIGEM, formatarDolar, formatarPreco, resumirUso, type LinhaModelo, type PrecoIa, type UsoIa } from '../utils/custoIa'
import { formatarInteiro } from '../utils/formato'
import { lerNumero } from '../utils/numero'
import { intervaloDoPeriodo, intervaloMesPassado } from '../utils/periodo'

type Mes = 'atual' | 'passado'
const br = (iso: string) => iso.split('-').reverse().join('/')

/**
 * Uso e custo estimado do provedor escolhido: chamadas e tokens (exatos, informados pelo
 * provedor) × preço de lista de cada modelo (editável). O valor cobrado de verdade fica no
 * painel do provedor — o card aponta para ele.
 */
export function UsoIaCard(p: { provedor: ProvedorIa; produto: string; nome: string; modeloAtual: string; ondeVerGasto: string }) {
  const [mes, setMes] = useState<Mes>('atual')
  const [uso, setUso] = useState<UsoIa[] | null>(null)
  const [precos, setPrecos] = useState<PrecoIa[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [editando, setEditando] = useState<LinhaModelo | null>(null)

  const intervalo = useMemo(() => (mes === 'atual' ? intervaloDoPeriodo('mes') : intervaloMesPassado()), [mes])

  useEffect(() => {
    let cancelado = false
    setUso(null)
    Promise.all([usoIa(intervalo), listarPrecosIa()])
      .then(([u, pr]) => {
        if (cancelado) return
        setUso(u)
        setPrecos(pr)
        setErro(null)
      })
      .catch((e: Error) => !cancelado && setErro(e.message))
    return () => {
      cancelado = true
    }
  }, [intervalo])

  const r = useMemo(() => resumirUso(uso ?? [], precos, p.provedor, [p.modeloAtual]), [uso, precos, p.provedor, p.modeloAtual])

  const recarregarPrecos = async () => setPrecos(await listarPrecosIa())

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        <Box sx={{ mr: 'auto' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Uso e custo estimado · {p.produto}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {br(intervalo.inicio)} a {br(intervalo.fim)}
          </Typography>
        </Box>
        <ToggleButtonGroup exclusive size="small" value={mes} onChange={(_, v: Mes | null) => v && setMes(v)}>
          <ToggleButton value="atual" sx={{ textTransform: 'none', px: 1.5 }}>
            Este mês
          </ToggleButton>
          <ToggleButton value="passado" sx={{ textTransform: 'none', px: 1.5 }}>
            Mês passado
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {erro ? <Alert severity="error">{erro}</Alert> : null}

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, opacity: uso ? 1 : 0.5 }}>
        <Numero rotulo="Custo estimado" valor={uso ? formatarDolar(r.custo) : '…'} destaque />
        <Numero rotulo="Chamadas" valor={uso ? formatarInteiro(r.chamadas) : '…'} />
        <Numero rotulo="Tokens enviados" valor={uso ? formatarInteiro(r.tokensEntrada) : '…'} />
        <Numero rotulo="Tokens recebidos" valor={uso ? formatarInteiro(r.tokensSaida) : '…'} />
      </Box>

      {r.semPreco.length ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Sem preço cadastrado para {r.semPreco.join(', ')}: o custo estimado não inclui esse uso. Cadastre o preço na tabela abaixo.
        </Alert>
      ) : null}

      {r.porOrigem.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {r.porOrigem.map((o) => `${NOME_ORIGEM[o.origem]}: ${formatarDolar(o.custo)} (${formatarInteiro(o.chamadas)} ${o.chamadas === 1 ? 'chamada' : 'chamadas'})`).join(' · ')}
        </Typography>
      ) : uso ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Nenhuma chamada a {p.produto} neste período.
        </Typography>
      ) : null}

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Modelo</TableCell>
              <TableCell align="right">Chamadas</TableCell>
              <TableCell align="right">Tokens enviados</TableCell>
              <TableCell align="right">Tokens recebidos</TableCell>
              <TableCell align="right">Preço por milhão (envio · retorno)</TableCell>
              <TableCell align="right">Custo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {r.porModelo.map((m) => (
              <TableRow key={m.modelo}>
                <TableCell sx={{ fontWeight: m.modelo === p.modeloAtual ? 700 : 400 }}>
                  {m.modelo}
                  {m.modelo === p.modeloAtual ? (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}
                      (configurado)
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">{formatarInteiro(m.chamadas)}</TableCell>
                <TableCell align="right">{formatarInteiro(m.tokensEntrada)}</TableCell>
                <TableCell align="right">{formatarInteiro(m.tokensSaida)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {m.preco ? `${formatarPreco(m.preco.entrada)} · ${formatarPreco(m.preco.saida)}` : 'sem preço'}
                  <Button size="small" onClick={() => setEditando(m)} sx={{ textTransform: 'none', ml: 0.5, minWidth: 0 }}>
                    {m.preco ? 'editar' : 'cadastrar'}
                  </Button>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {m.custo == null ? '—' : formatarDolar(m.custo)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
        Estimativa: tokens informados pelo provedor em cada chamada × preço de lista do modelo (padrão, sem desconto de lote ou cache). Chamadas
        com erro e leituras que já estavam salvas não entram. A contagem começou em 02/10/2026. O valor cobrado de verdade está no{' '}
        <Link href={p.ondeVerGasto} target="_blank" rel="noopener noreferrer">
          painel da {p.nome}
        </Link>
        .
      </Typography>

      {editando ? (
        <EditarPreco
          provedor={p.provedor}
          linha={editando}
          onFechar={() => setEditando(null)}
          onSalvo={async () => {
            setEditando(null)
            await recarregarPrecos()
          }}
        />
      ) : null}
    </Paper>
  )
}

function Numero({ rotulo, valor, destaque }: { rotulo: string; valor: string; destaque?: boolean }) {
  return (
    <Box sx={{ border: 1, borderColor: destaque ? 'primary.main' : 'divider', borderRadius: 1, px: 1.5, py: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
        {rotulo}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {valor}
      </Typography>
    </Box>
  )
}

function EditarPreco({ provedor, linha, onFechar, onSalvo }: { provedor: string; linha: LinhaModelo; onFechar: () => void; onSalvo: () => void }) {
  const [entrada, setEntrada] = useState(linha.preco ? String(linha.preco.entrada).replace('.', ',') : '')
  const [saida, setSaida] = useState(linha.preco ? String(linha.preco.saida).replace('.', ',') : '')
  const [fonte, setFonte] = useState(linha.preco?.fonte ?? '')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const e = lerNumero(entrada)
  const s = lerNumero(saida)
  const valido = e != null && s != null && !Number.isNaN(e) && !Number.isNaN(s) && e >= 0 && s >= 0 && (!fonte.trim() || fonte.trim().startsWith('https://'))

  const salvar = async () => {
    if (!valido) return
    setSalvando(true)
    setErro(null)
    try {
      const hoje = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date())
      await salvarPrecoIa({ provedor, modelo: linha.modelo, entrada: e, saida: s, fonte: fonte.trim() || null, conferidoEm: hoje })
      onSalvo()
    } catch (x) {
      setErro((x as Error).message)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open onClose={onFechar} fullWidth maxWidth="xs">
      <DialogTitle>Preço de {linha.modelo}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Em dólares por milhão de tokens, como na página de preços do provedor (preço padrão).
            {linha.preco?.conferidoEm ? ` Conferido em ${br(linha.preco.conferidoEm)}.` : ''}
          </Typography>
          <TextField label="Envio (entrada), US$ por milhão" value={entrada} onChange={(x) => setEntrada(x.target.value)} inputMode="decimal" />
          <TextField label="Retorno (saída), US$ por milhão" value={saida} onChange={(x) => setSaida(x.target.value)} inputMode="decimal" />
          <TextField
            label="Fonte (opcional)"
            value={fonte}
            onChange={(x) => setFonte(x.target.value)}
            placeholder="https://…"
            helperText={linha.preco?.fonte ? <Link href={linha.preco.fonte} target="_blank" rel="noopener noreferrer">Abrir a página de preços</Link> : 'A página de preços do provedor'}
          />
          {erro ? <Alert severity="error">{erro}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button variant="contained" disabled={!valido || salvando} onClick={salvar} sx={{ textTransform: 'none' }}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
